// Screens that open as a MODAL OVER a mission instead of navigating away — CCT-346.
//
// ⚠️ WHY NOT SIMPLY NAVIGATE. Leaving the mission screen to read a notification, a conversation
// or the board does three things the player never asked for: the MUSIC stops (the track follows
// the mission screen's FOCUS, not its mounting — CCT-146, and that rule is right: a mission left
// behind must go quiet), the mission's own state (open element, scroll, progression) is put aside,
// and coming back means finding one's way there again. A three-second glance at the board cost the
// atmosphere of the scene being read.
//
// So while a mission is on screen, these three open OVER it: nothing is unmounted, nothing loses
// focus, the music keeps playing, and closing puts the player back exactly where they were.
//
// ⚠️ REGISTRATION FOLLOWS THE FOCUS, NOT THE MOUNTING. The mission screen STAYS MOUNTED in the
// stack once the player has left it (that is precisely why the music hook watches focus). A
// registration tied to mounting would keep answering long after: clicking "Chat" from the mission
// list would open a panel over an invisible screen — a click that appears to do nothing at all.

export type MissionPanel = 'Notifications' | 'Chat' | 'Dashboard';

/** The three, and only these three. Anything else navigates as before. */
export const MISSION_PANELS: readonly MissionPanel[] = ['Notifications', 'Chat', 'Dashboard'];

type Opener = (panel: MissionPanel) => void;

//: At most one mission is on screen at a time, so one slot is enough. `null` = no mission in
//: sight, and every caller then falls back to normal navigation.
let opener: Opener | null = null;

/** Declared by the mission screen WHILE IT IS FOCUSED. Returns the deregistration. */
export function registerMissionPanels(fn: Opener): () => void {
  opener = fn;
  return () => { if (opener === fn) opener = null; };
}

/**
 * Open `name` over the current mission. Returns `false` when there is no mission on screen or the
 * destination is not one of the three — the caller then navigates normally.
 *
 * ⚠️ THE RETURN VALUE IS THE WHOLE CONTRACT: a caller that ignores it either navigates on top of
 * the panel it just opened, or swallows the click.
 */
export function openInMission(name: string): boolean {
  if (!opener || !MISSION_PANELS.includes(name as MissionPanel)) return false;
  opener(name as MissionPanel);
  return true;
}
